import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';


function WorkingHoursManager({ companyId }) {
  const [workingHours, setWorkingHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const daysOfWeek = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
  ];

  useEffect(() => {
    if (companyId) {
      fetchWorkingHours();
    } else {
      setLoading(false);
      setError('Please select a company first');
    }
  }, [companyId]);

  const fetchWorkingHours = async () => {
    if (!companyId) {
      setError('Company ID is required');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await bookingAPI.getWorkingHours(companyId);
      const hours = response.data.working_hours || [];
      
      const initializedHours = daysOfWeek.map(day => {
        const existing = hours.find(wh => wh.day_of_week === day.value);
        if (existing) return existing;
        return {
          day_of_week: day.value,
          is_working_day: day.value < 5,
          start_time: day.value < 5 ? '09:00:00' : null,
          end_time: day.value < 5 ? '17:00:00' : null,
          break_start: null,
          break_end: null
        };
      });

      setWorkingHours(initializedHours);
      setError('');
    } catch (err) {
      setError('Failed to load working hours');
    } finally {
      setLoading(false);
    }
  };

  const updateDay = (dayOfWeek, field, value) => {
    setWorkingHours(prev => prev.map(wh => {
      if (wh.day_of_week === dayOfWeek) return { ...wh, [field]: value };
      return wh;
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      // Validate working hours before sending
      for (const wh of workingHours) {
        if (wh.is_working_day) {
          if (!wh.start_time || !wh.end_time) {
            setError('Please set start and end times for all working days');
            setSaving(false);
            return;
          }
          // Ensure time format is correct for comparison
          const startStr = wh.start_time.substring(0, 5);
          const endStr = wh.end_time.substring(0, 5);
          const start = new Date(`2000-01-01T${startStr}`);
          const end = new Date(`2000-01-01T${endStr}`);
          if (end <= start) {
            setError(`End time must be after start time for ${daysOfWeek.find(d => d.value === wh.day_of_week)?.label}`);
            setSaving(false);
            return;
          }
        }
      }

      // Ensure all times are in HH:MM:SS format
      const formattedHours = workingHours.map(wh => ({
        ...wh,
        start_time: formatTimeForAPI(wh.start_time),
        end_time: formatTimeForAPI(wh.end_time),
        break_start: formatTimeForAPI(wh.break_start),
        break_end: formatTimeForAPI(wh.break_end)
      }));

      await bookingAPI.updateWorkingHours(formattedHours, companyId);
      setSuccess('Working hours updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Working hours update error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update working hours';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatTimeForInput = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const formatTimeForAPI = (timeString) => {
    if (!timeString) return null;
    return timeString.length === 5 ? `${timeString}:00` : timeString;
  };

  if (loading) {
    return <div className="loading">Loading working hours...</div>;
  }

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'var(--bg-overlay-medium)',
    color: '#ffffff',
    fontSize: '14px'
  };

  return (
    <div>
      <h3 style={{ margin: '0 0 8px 0', color: '#ffffff', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span>🕐</span> Working Hours
      </h3>
      <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', marginBottom: '20px' }}>
        Set your company's working hours for each day
      </p>

      {error && <div className="error" style={{marginBottom: '16px'}}>{error}</div>}
      {success && <div className="success" style={{marginBottom: '16px'}}>{success}</div>}

      <form onSubmit={handleSave}>
        <div style={{ display: 'grid', gap: '12px' }}>
          {workingHours.map(wh => {
            const day = daysOfWeek.find(d => d.value === wh.day_of_week);
            return (
              <div
                key={wh.day_of_week}
                style={{
                  padding: '16px',
                  borderRadius: '12px',
                  border: `1px solid ${wh.is_working_day ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.05)'}`,
                  background: wh.is_working_day ? 'rgba(16, 185, 129, 0.05)' : 'rgba(255, 255, 255, 0.02)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: wh.is_working_day ? '16px' : '0' }}>
                  <input
                    type="checkbox"
                    checked={wh.is_working_day}
                    onChange={(e) => updateDay(wh.day_of_week, 'is_working_day', e.target.checked)}
                    style={{ marginRight: '12px', width: '18px', height: '18px', accentColor: 'var(--lime-primary)' }}
                  />
                  <label style={{ fontSize: '15px', fontWeight: '600', color: '#ffffff', flex: 1 }}>
                    {day?.label}
                  </label>
                  {!wh.is_working_day && (
                    <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '13px' }}>Closed</span>
                  )}
                </div>

                {wh.is_working_day && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginLeft: '30px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>Start</label>
                      <input
                        type="time"
                        value={formatTimeForInput(wh.start_time)}
                        onChange={(e) => updateDay(wh.day_of_week, 'start_time', formatTimeForAPI(e.target.value))}
                        required
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>End</label>
                      <input
                        type="time"
                        value={formatTimeForInput(wh.end_time)}
                        onChange={(e) => updateDay(wh.day_of_week, 'end_time', formatTimeForAPI(e.target.value))}
                        required
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>Break Start</label>
                      <input
                        type="time"
                        value={formatTimeForInput(wh.break_start)}
                        onChange={(e) => updateDay(wh.day_of_week, 'break_start', formatTimeForAPI(e.target.value))}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>Break End</label>
                      <input
                        type="time"
                        value={formatTimeForInput(wh.break_end)}
                        onChange={(e) => updateDay(wh.day_of_week, 'break_end', formatTimeForAPI(e.target.value))}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Working Hours'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={fetchWorkingHours} disabled={saving}>
            Reset
          </button>
        </div>
      </form>
    </div>
  );
}

export default WorkingHoursManager;
